import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Shield,
  Bell,
  Clock,
  Plus,
  Trash2,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Activity,
  Mail,
  Eye,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FRAMEWORKS = [
  { id: "HIPAA", name: "HIPAA" },
  { id: "SOC2", name: "SOC 2" },
  { id: "NIST", name: "NIST" },
  { id: "OWASP", name: "OWASP" },
  { id: "CIS", name: "CIS" },
  { id: "HITRUST", name: "HITRUST" }
];

export default function Monitoring({ user, logout, token }) {
  const navigate = useNavigate();
  const [monitors, setMonitors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newMonitor, setNewMonitor] = useState({
    name: "",
    scan_type: "full",
    target_url: "",
    target_ip: "",
    compliance_frameworks: ["HIPAA"],
    frequency_hours: 12,
    alert_on_critical: true,
    alert_on_high: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [monitorsRes, alertsRes, statusRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/monitoring`, { headers, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/alerts`, { headers, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/monitoring/status`, { headers, withCredentials: true })
      ]);
      setMonitors(monitorsRes.data);
      setAlerts(alertsRes.data);
      setStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
      toast.error("Failed to load monitoring data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMonitor = async () => {
    if (!newMonitor.name) {
      toast.error("Please enter a monitor name");
      return;
    }

    setCreating(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${BACKEND_URL}/api/monitoring`, {
        name: newMonitor.name,
        config: {
          scan_type: newMonitor.scan_type,
          target_url: newMonitor.target_url || null,
          target_ip: newMonitor.target_ip || null,
          compliance_frameworks: newMonitor.compliance_frameworks,
          scan_depth: "standard"
        },
        frequency_hours: newMonitor.frequency_hours,
        alert_on_critical: newMonitor.alert_on_critical,
        alert_on_high: newMonitor.alert_on_high
      }, { headers, withCredentials: true });
      
      toast.success("Monitoring schedule created!");
      setCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to create monitoring schedule");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleMonitor = async (monitorId, isActive) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.patch(`${BACKEND_URL}/api/monitoring/${monitorId}`, {
        is_active: !isActive
      }, { headers, withCredentials: true });
      
      toast.success(isActive ? "Monitoring paused" : "Monitoring activated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update monitor");
    }
  };

  const handleDeleteMonitor = async (monitorId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${BACKEND_URL}/api/monitoring/${monitorId}`, { headers, withCredentials: true });
      toast.success("Monitor deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete monitor");
    }
  };

  const handleMarkAlertRead = async (alertId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.patch(`${BACKEND_URL}/api/alerts/${alertId}/read`, {}, { headers, withCredentials: true });
      fetchData();
    } catch (error) {
      toast.error("Failed to mark alert as read");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${BACKEND_URL}/api/alerts/mark-all-read`, {}, { headers, withCredentials: true });
      toast.success("All alerts marked as read");
      fetchData();
    } catch (error) {
      toast.error("Failed to mark alerts as read");
    }
  };

  const getAlertSeverityColor = (severity) => {
    const colors = {
      critical: "text-red-500 bg-red-500/10 border-red-500/20",
      high: "text-orange-500 bg-orange-500/10 border-orange-500/20",
      info: "text-blue-500 bg-blue-500/10 border-blue-500/20"
    };
    return colors[severity] || colors.info;
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-primary">Loading monitoring...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="monitoring-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold font-[Chivo]">24/7 Security Monitoring</h2>
            <p className="text-muted-foreground">
              Automated security scans with real-time alerts
            </p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="glow-primary" data-testid="create-monitor-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Monitor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create Monitoring Schedule</DialogTitle>
                <DialogDescription>
                  Set up automated security scans that run on a schedule
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Monitor Name</Label>
                  <Input
                    placeholder="Production Environment Monitor"
                    value={newMonitor.name}
                    onChange={(e) => setNewMonitor({ ...newMonitor, name: e.target.value })}
                    data-testid="monitor-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Scan Type</Label>
                  <Select
                    value={newMonitor.scan_type}
                    onValueChange={(v) => setNewMonitor({ ...newMonitor, scan_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="web">Web Application</SelectItem>
                      <SelectItem value="network">Network</SelectItem>
                      <SelectItem value="compliance">Compliance Only</SelectItem>
                      <SelectItem value="full">Full Assessment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(newMonitor.scan_type === 'web' || newMonitor.scan_type === 'full') && (
                  <div className="space-y-2">
                    <Label>Target URL</Label>
                    <Input
                      placeholder="https://app.example.com"
                      value={newMonitor.target_url}
                      onChange={(e) => setNewMonitor({ ...newMonitor, target_url: e.target.value })}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Scan Frequency</Label>
                  <Select
                    value={String(newMonitor.frequency_hours)}
                    onValueChange={(v) => setNewMonitor({ ...newMonitor, frequency_hours: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">Every 6 hours</SelectItem>
                      <SelectItem value="12">Every 12 hours</SelectItem>
                      <SelectItem value="24">Every 24 hours</SelectItem>
                      <SelectItem value="168">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label>Alert Settings</Label>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Alert on Critical vulnerabilities</span>
                    <Switch
                      checked={newMonitor.alert_on_critical}
                      onCheckedChange={(c) => setNewMonitor({ ...newMonitor, alert_on_critical: c })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Alert on High vulnerabilities</span>
                    <Switch
                      checked={newMonitor.alert_on_high}
                      onCheckedChange={(c) => setNewMonitor({ ...newMonitor, alert_on_high: c })}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateMonitor} disabled={creating}>
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Create Monitor
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Status Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border" data-testid="status-active-monitors">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Monitors</p>
                  <p className="text-3xl font-bold font-[Chivo] text-primary">
                    {status?.active_monitors || 0}
                  </p>
                </div>
                <Activity className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border" data-testid="status-unread-alerts">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Unread Alerts</p>
                  <p className="text-3xl font-bold font-[Chivo] text-orange-500">
                    {status?.unread_alerts || 0}
                  </p>
                </div>
                <Bell className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border" data-testid="status-critical-alerts">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Critical Alerts</p>
                  <p className="text-3xl font-bold font-[Chivo] text-red-500">
                    {status?.critical_alerts || 0}
                  </p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border" data-testid="status-email-configured">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Email Alerts</p>
                  <p className="text-lg font-bold font-[Chivo]">
                    {status?.email_alerts_configured ? (
                      <span className="text-emerald-500">Configured</span>
                    ) : (
                      <span className="text-yellow-500">Pending Setup</span>
                    )}
                  </p>
                </div>
                <Mail className={`h-8 w-8 ${status?.email_alerts_configured ? 'text-emerald-500' : 'text-yellow-500'}`} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Monitoring Schedules */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-[Chivo]">Monitoring Schedules</CardTitle>
            <CardDescription>
              Your automated security scan schedules
            </CardDescription>
          </CardHeader>
          <CardContent>
            {monitors.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No monitors configured</h3>
                <p className="text-muted-foreground mb-4">
                  Set up your first monitoring schedule to enable 24/7 security scanning
                </p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Monitor
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {monitors.map((monitor, index) => (
                  <motion.div
                    key={monitor.monitor_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center justify-between p-4 border rounded-sm ${
                      monitor.is_active ? 'border-primary/30 bg-primary/5' : 'border-border'
                    }`}
                    data-testid={`monitor-${monitor.monitor_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${
                        monitor.is_active ? 'bg-primary/20' : 'bg-muted'
                      }`}>
                        <Activity className={`h-5 w-5 ${monitor.is_active ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <p className="font-medium">{monitor.name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span>Every {monitor.frequency_hours}h</span>
                          <span>•</span>
                          <span className="capitalize">{monitor.config?.scan_type || 'full'}</span>
                          {monitor.next_run && (
                            <>
                              <span>•</span>
                              <span>Next: {new Date(monitor.next_run).toLocaleString()}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        {monitor.alert_on_critical && (
                          <Badge variant="outline" className="text-red-500 border-red-500/30">Critical</Badge>
                        )}
                        {monitor.alert_on_high && (
                          <Badge variant="outline" className="text-orange-500 border-orange-500/30">High</Badge>
                        )}
                      </div>
                      <Switch
                        checked={monitor.is_active}
                        onCheckedChange={() => handleToggleMonitor(monitor.monitor_id, monitor.is_active)}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteMonitor(monitor.monitor_id)}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Alerts */}
        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-[Chivo]">Recent Alerts</CardTitle>
              <CardDescription>
                Security alerts from your monitoring scans
              </CardDescription>
            </div>
            {alerts.some(a => !a.is_read) && (
              <Button variant="outline" size="sm" onClick={handleMarkAllRead}>
                <Eye className="h-4 w-4 mr-2" />
                Mark All Read
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
                <p className="text-muted-foreground">No alerts - your systems are secure!</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {alerts.map((alert, index) => (
                    <motion.div
                      key={alert.alert_id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className={`p-4 border rounded-sm ${getAlertSeverityColor(alert.severity)} ${
                        !alert.is_read ? 'border-l-4' : ''
                      }`}
                      data-testid={`alert-${alert.alert_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {alert.severity === 'critical' ? (
                            <AlertCircle className="h-5 w-5 mt-0.5" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 mt-0.5" />
                          )}
                          <div>
                            <p className="font-medium">{alert.title}</p>
                            <p className="text-sm opacity-80 mt-1">{alert.message}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs opacity-60">
                              <span>{new Date(alert.created_at).toLocaleString()}</span>
                              {alert.email_sent && (
                                <>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    Email sent
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!alert.is_read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkAlertRead(alert.alert_id)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/scan/${alert.scan_id}`)}
                          >
                            View Scan
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
